import React, { useEffect, useMemo, useRef, useState } from "react";
import allSkills from "../../config/allSkill.json";
import { AllSkills, SkillObj } from "../../config/metadata";

interface SkillSelectorProps {
  label?: string;
  selected: SkillObj[];
  onChange: (skills: SkillObj[]) => void;
  required?: boolean;
  placeholder?: string;
  excluded?: SkillObj[]; // skills that must not appear/selectable (e.g., required skills)
}

const normalize = (s: string) => s.toLowerCase();
const comparator = (a: SkillObj, b: SkillObj) =>
  a.skillId == b.skillId ? 0 : a.skillId < b.skillId ? -1 : 1;
const SkillSelector: React.FC<SkillSelectorProps> = ({
  label,
  selected,
  onChange,
  required = false,
  placeholder = "Type to search skills...",
  excluded = [],
}) => {
  const [query, setQuery] = useState("");
  const [open, setOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (!containerRef.current) return;
      if (!containerRef.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  const selectedSet = useMemo(() => new Set(selected), [selected]);
  const excludedSet = useMemo(() => new Set(excluded), [excluded]);

  // Normalize dataset: support either string[] or { skills: { skillName: string }[] }
  const relatedMap: Record<number, SkillObj[]> = useMemo(() => {
    const map: Record<number, SkillObj[]> = {};
    allSkills.skills.forEach((skill) => {
      skill.categories.forEach((categoryId) => {
        if (map[categoryId]) {
          map[categoryId] = [...map[categoryId], skill];
        } else {
          map[categoryId] = [skill];
        }
      });
    });
    return map;
  }, []);
  const allSkillNames: SkillObj[] = allSkills.skills

  const [suggestions,setSuggestion] = useState<SkillObj[]>([]);
  useEffect(() => {
    const q = normalize(query).trim();
    // if (!q || q=='') setSuggestion([]);
    if (q && q != ''){
      const matches: SkillObj[] = [];
      // ADD for limiting number of suggestion
      for (let i = 0; i < allSkillNames.length && matches.length < 50; i += 1) {
        const skill = allSkillNames[i];
        // const s = normalize(skill || '');
        if (
          skill.skillName.toLowerCase().includes(q) &&
          !selectedSet.has(allSkillNames[i]) &&
          !excludedSet.has(allSkillNames[i])
        )
        matches.push(skill);
      }
      setSuggestion(matches);
    }
  }, [query]);

  const [relatedSuggestions, setRelatedSuggestions] = useState<SkillObj[]>([]);
  // Related suggestions shown when there is no query but user has selected skills
  useEffect(() => {
    if (query.trim()) {
      // setRelatedSuggestions([]);
      return;
    }
    if (selected.length === 0) {
      setRelatedSuggestions([]);
      return;
    }
    // Curated adjacency map for common ecosystems

    const pool = new Set<SkillObj>();
    const addedCategory: number[] = [];
    // const universe = new Set(allSkillNames.map(normalize));
    for (const picked of selected) {
      picked.categories.forEach((category) => {
        if (addedCategory.includes(category)) return;
        addedCategory.push(category);
        const related = relatedMap[category] || [];
        for (const skill of related) {
          if (!selectedSet.has(skill) && !excludedSet.has(skill)) {
            pool.add(skill);
          }
        }

        // Heuristic: add skills that share tokens/prefixes
        // const token = key.split(/[^a-z0-9]+/)[0];
        // if (token && token.length >= 3) {
        //   for (let i = 0; i < allSkillNames.length && pool.size < 30; i += 1) {
        //     const skill = allSkillNames[i];
        //     const s = normalize(skill);
        //     if (!selectedSet.has(s) && !excludedSet.has(s) && (s.startsWith(token) || s.includes(token + ' '))) {
        //       pool.add(skill);
        //     }
        //   }
        // }
      });
    }
    setRelatedSuggestions(Array.from(pool));
  }, [query,selectedSet]);

  // Default suggestions when input is empty and no related picks yet
  const defaultSuggestions: SkillObj[] = useMemo(() => {
    if (query.trim()) return [];
    // TODO add Popular to skillObj mapping
    // // Curated popular skills (case-insensitive match against dataset)
    // const popular = [
    //   // Web fundamentals
    //   'React', 'JavaScript', 'TypeScript', 'HTML', 'CSS', 'Tailwind CSS', 'Sass', 'SCSS',
    //   // Frontend frameworks & tools
    //   'Next.js', 'Redux', 'Redux Toolkit', 'Zustand', 'React Query', 'TanStack Query', 'SWR',
    //   'React Native', 'Vite', 'Webpack', 'Babel', 'Cypress', 'Playwright', 'Jest', 'React Testing Library', 'Storybook', 'ESLint', 'Prettier',
    //   'Ant Design', 'Material UI', 'Chakra UI',
    //   // Node & backend JS
    //   'Node.js', 'Express', 'NestJS', 'GraphQL', 'Apollo', 'Prisma', 'tRPC', 'REST API',
    //   // Databases & streaming
    //   'MongoDB', 'PostgreSQL', 'MySQL', 'Redis', 'ElasticSearch', 'Kafka', 'SQLite', 'Supabase',
    //   // Cloud & DevOps
    //   'AWS', 'Azure', 'GCP', 'Docker', 'Kubernetes', 'Terraform', 'Jenkins', 'GitHub Actions', 'CircleCI', 'CI/CD', 'Helm', 'Ansible',
    //   'Nginx', 'Linux', 'Shell Scripting',
    //   // Other popular ecosystems
    //   'Python', 'Django', 'Flask', 'FastAPI', 'Pandas', 'NumPy',
    //   'Java', 'Spring Boot', 'Hibernate',
    //   'Go', 'Rust',
    //   'PHP', 'Laravel',
    //   'Ruby on Rails',
    //   // Additional SPA frameworks
    //   'Angular', 'RxJS', 'NgRx', 'Vue', 'Pinia', 'Vuex', 'Svelte',
    //   // Mobile & cross-platform
    //   'Flutter', 'Swift', 'Kotlin',
    //   // Security & auth
    //   'JWT', 'OAuth',
    //   // Testing styles
    //   'Unit Testing', 'Integration Testing', 'E2E Testing'
    // ];
    const resolved: SkillObj[] = [];
    const suggestionPerCategory = 2; // controll how many skill per category are suggested
    const mappedInResolved : Map<number,number> = new Map();
    if (resolved.length < 48) {
      for (
        let i = 0;
        i < allSkillNames.length && resolved.length < 48;
        i += 1
      ) {
        const sn = allSkillNames[i];
        if (
          !selectedSet.has(sn) &&
          !excludedSet.has(sn) &&
          !resolved.includes(sn)
        ){
          const matched = mappedInResolved.get(sn.categories[0]) || 0; 
          if(matched < suggestionPerCategory){
            mappedInResolved.set(sn.categories[0], matched + 1);
            resolved.push(sn);
          }
        }
      }
    }
    return resolved;
  }, [selectedSet, excludedSet]);

  const addSkill = (skill: SkillObj) => {
    // Prevent adding excluded skills
    if (excludedSet.has(skill)) return;
    const next = Array.from(new Set([...selected, skill]));
    onChange(next);
    setQuery("");
    // setOpen(false);
    // re-focus for fast entry
    setTimeout(() => inputRef.current?.focus(), 0);
  };

  const removeSkill = (skill: SkillObj) => {
    onChange(selected.filter((s) => s.skillId !== skill.skillId));
  };

  const handleKeyDown: React.KeyboardEventHandler<HTMLInputElement> = (e) => {
    if (e.key === "Enter" || e.key === ",") {
      e.preventDefault();
      // TODO check keydown
      // if (query.trim())  addSkill(query);
    } else if (e.key === "Backspace" && !query && selected.length > 0) {
      // Remove last chip when input empty
      removeSkill(selected[selected.length - 1]);
    }
  };

  return (
    <div className="w-full" ref={containerRef}>
      {label && (
        <label className="block text-sm font-medium text-gray-700 mb-2">
          {label}
          {required ? " *" : ""}
        </label>
      )}
      <div
        className={`w-full min-h-[48px] px-4 py-3 border-2 rounded-xl transition-all duration-200 flex flex-wrap items-center gap-2 ${
          open
            ? "border-green-500 bg-green-50 shadow-lg ring-4 ring-green-100"
            : "border-gray-200 bg-white hover:border-gray-300 hover:shadow-sm focus-within:ring-4 focus-within:ring-green-100 focus-within:border-green-500"
        }`}
        onClick={() => inputRef.current?.focus()}
      >
        {selected.map((skill) => (
          <span
            key={skill.skillId}
            className="inline-flex items-center px-3 py-1.5 rounded-full text-sm bg-green-100 text-green-800 border border-green-200 font-medium"
          >
            {skill.skillName}
            <button
              type="button"
              className="ml-2 text-green-700 hover:text-green-900 hover:bg-green-200 rounded-full p-0.5 transition-colors duration-150"
              onClick={() => removeSkill(skill)}
              aria-label={`Remove ${skill}`}
            >
              <svg
                className="w-3 h-3"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M6 18L18 6M6 6l12 12"
                />
              </svg>
            </button>
          </span>
        ))}
        <input
          ref={inputRef}
          value={query}
          onChange={(e) => {
            setQuery(e.target.value);
            setOpen(true);
          }}
          onFocus={() => setOpen(true)}
          onKeyDown={handleKeyDown}
          placeholder={
            selected.length === 0 ? placeholder : "Add another skill..."
          }
          className="flex-1 min-w-[140px] outline-none text-sm text-gray-800 placeholder:text-gray-400 py-1 bg-transparent"
        />
        <svg
          className={`w-5 h-5 text-gray-400 transition-transform duration-200 ${
            open ? "rotate-180 text-green-500" : ""
          }`}
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M19 9l-7 7-7-7"
          />
        </svg>
      </div>

      {open &&
        (suggestions.length > 0 ||
          relatedSuggestions.length > 0 ||
          defaultSuggestions.length > 0 ||
          query.trim()) && (
          <div className="mt-2 bg-white rounded-xl shadow-2xl border border-gray-100 overflow-hidden animate-in fade-in-0 zoom-in-95 duration-200 z-50">
            {relatedSuggestions.length > 0 && !query.trim() && (
              <>
                <div className="p-3 border-b border-gray-100 bg-gray-50">
                  <div className="text-xs text-gray-500 font-medium">
                    Related to your selection
                  </div>
                </div>
                <div className="max-h-64 overflow-auto">
                  {relatedSuggestions.map((skill) => (
                    <button
                      key={`rel-${skill.skillId}`}
                      type="button"
                      onClick={() => addSkill(skill)}
                      className="w-full text-left px-4 py-3 transition-colors duration-150 text-gray-800 hover:bg-gray-50 hover:text-gray-900 border-b border-gray-50 last:border-b-0"
                    >
                      <div className="flex items-center">
                        <svg
                          className="w-4 h-4 mr-3 text-emerald-500"
                          fill="none"
                          stroke="currentColor"
                          viewBox="0 0 24 24"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M5 13l4 4L19 7"
                          />
                        </svg>
                        <span className="font-medium">{skill.skillName}</span>
                      </div>
                    </button>
                  ))}
                </div>
                {(query.trim()) && (
                  <div className="p-2 border-t border-gray-100 bg-gray-50 text-[10px] text-gray-400 uppercase tracking-wider">
                    Search results
                  </div>
                )}
              </>
            )}
            {query.trim() && (
              <div className="p-3 border-b border-gray-100 bg-gray-50">
                <div className="text-xs text-gray-500 font-medium">
                  Skill Suggestions
                </div>
              </div>
            )}
            {!query.trim() &&
              relatedSuggestions.length === 0 &&
              defaultSuggestions.length > 0 && (
                <>
                  <div className="p-3 border-b border-gray-100 bg-gray-50">
                    <div className="text-xs text-gray-500 font-medium">
                      Suggested skills
                    </div>
                  </div>
                  <div className="max-h-64 overflow-auto p-3">
                    <div className="grid grid-cols-3 gap-2 md:grid-cols-4 lg:grid-cols-5">
                      {defaultSuggestions.map((skill) => (
                        <button
                          key={`def-${skill.skillId}`}
                          type="button"
                          onClick={() => addSkill(skill)}
                          // className="text-left px-3 py-2 rounded-lg border border-gray-200 bg-white text-gray-800 hover:border-green-300 hover:bg-green-50 hover:text-green-800 transition-colors duration-150"
                          className="text-left px-3 py-1.5 rounded-full bg-white text-gray-800 border border-gray-200 font-small hover:border-green-300 hover:bg-green-50 hover:text-green-800 transition-colors duration-150"
                        >
                          <div className="flex items-center">
                             {/* TODO fix size issue */}
                            {/* <svg
                              className="w-4 h-4 mr-2 text-green-500"
                              fill="none"
                              stroke="currentColor"
                              viewBox="0 0 24 24"
                            >
                              <path
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                strokeWidth={2}
                                d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z"
                              />
                            </svg> */}
                            <span className="font-small truncate">
                              {skill.skillName}
                            </span>
                          </div>
                        </button>
                      ))}
                    </div>
                  </div>
                </>
              )}
            { query.trim() &&
              <div className="max-h-64 overflow-auto">
                {suggestions.map((skill) => (
                  <button
                    key={`${skill.skillId}`}
                    type="button"
                    onClick={() => addSkill(skill)}
                    className="w-full text-left px-4 py-3 transition-colors duration-150 text-gray-800 hover:bg-gray-50 hover:text-gray-900 border-b border-gray-50 last:border-b-0"
                  >
                    <div className="flex items-center">
                      <svg
                        className="w-4 h-4 mr-3 text-green-500"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z"
                        />
                      </svg>
                      <span className="font-medium">{skill.skillName}</span>
                    </div>
                  </button>
                ))}
              </div>
            }
          </div>
        )}
    </div>
  );
};

export default SkillSelector;
